import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'

import {
   IconButton,
   IconButtonHierarchy,
   IconButtonSubVariant
} from '@flowwai-frontend/design-system'
import {
   ChatVirtualAssistantIcon,
   ClockFastBackwardIcon
} from '@flowwai-frontend/design-system/icons'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { ChatBoxTextField } from '@flowwai-frontend/shared-web/ai-chat-support/ui'
import { useUserAppConfigStore } from '@flowwai-frontend/bps-officer-web/iam'

import * as Styles from './styles'

interface NewThreadControllerProps {
   onSendChat: (chatQuery: string) => void
   renderActionButtons: () => React.ReactElement
   isChatBoxExpanded: boolean
   shouldShowChatHistoryInSidepeek: boolean
   isThreadsAvailable: boolean
   onClickHistoryInExpandedView: () => void
}

const NewThreadController = (
   props: NewThreadControllerProps
): React.ReactElement => {
   const {
      onSendChat,
      renderActionButtons,
      isChatBoxExpanded,
      shouldShowChatHistoryInSidepeek,
      isThreadsAvailable,
      onClickHistoryInExpandedView
   } = props

   const { theme } = useAppTheme()
   const t = useCustomT('aiChatSupport:chatBox')
   const chatBoxTheme = theme.aiChatSupport.chatBox
   const userStore = useUserAppConfigStore()

   const renderHeaderTextWithDescription = (): React.ReactElement => (
      <div className={cn(Styles.headerContainerStyles)}>
         <p
            className={cn(
               Styles.headerTextStyles,
               chatBoxTheme.headerTextColor
            )}
         >
            {t('greetingText', { userName: userStore.userDetails?.name ?? '' })}
         </p>
         <p
            className={cn(
               Styles.descriptionStyles,
               chatBoxTheme.descriptionTextColor
            )}
         >
            {t('descriptionText')}
         </p>
      </div>
   )

   const renderHeaderLeftActions = (): React.ReactElement => (
      <div>
         <IconButton
            hierarchy={IconButtonHierarchy.Tertiary}
            size={'Small'}
            subVariant={IconButtonSubVariant.Gray}
            icon={() => (
               <ClockFastBackwardIcon
                  height={20}
                  width={20}
                  fill={chatBoxTheme.historyIconColor}
               />
            )}
            onClick={onClickHistoryInExpandedView}
         />
      </div>
   )

   return (
      <div className={cn(Styles.containerStyles)}>
         <div
            className={cn('flex justify-between items-center', {
               'h-[36px]': isChatBoxExpanded
            })}
         >
            {isChatBoxExpanded &&
            !shouldShowChatHistoryInSidepeek &&
            isThreadsAvailable
               ? renderHeaderLeftActions()
               : null}
            <div className='w-full flex justify-end'>
               {renderActionButtons()}
            </div>
         </div>

         <div className={Styles.bodyContainerStyles}>
            <div className='mb-2'>
               <ChatVirtualAssistantIcon
                  fill={chatBoxTheme.chatVirtualAssistantIconColor}
               />
            </div>
            {renderHeaderTextWithDescription()}
         </div>

         <ChatBoxTextField
            onClickSend={onSendChat}
            containerClassName='max-w-[776px] self-center'
            autoFocus
         />
      </div>
   )
}

export default observer(NewThreadController)
